import { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Image, useBreakpointValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import CaseLightbox from './CaseLightbox';
import { caseImages } from '../data/caseImages';

// Create motion components
const MotionBox = motion(Box);

// Space between images (px), both across and down
const GAP = 24;

/**
 * Loads every image to learn its height/width ratio. Resolves to null until all are known.
 */
function useImageRatios(sources: string[]) {
	const [ratios, setRatios] = useState<number[] | null>(null);

	useEffect(() => {
		let cancelled = false;

		const loadRatio = (src: string) => new Promise<number>((resolve) => {
			const img = new window.Image();
			img.onload = () => resolve(img.naturalHeight / img.naturalWidth);
			// Fall back to a square if an image fails, so the rest still lay out
			img.onerror = () => resolve(1);
			img.src = src;
		});

		Promise.all(sources.map(loadRatio)).then((result) => {
			if (!cancelled) setRatios(result);
		});

		return () => {
			cancelled = true;
		};
	}, [sources]);

	return ratios;
}

// Small allowance per image for the gap below it, relative to the column width
const GAP_ALLOWANCE = 0.05;

/**
 * Splits images (by height/width ratio) into columns of near-equal height.
 * Tallest images are placed first into the shortest column, then images are swapped
 * between the tallest and shortest columns while that brings them closer together.
 * Returns the image indexes for each column, in their original order.
 */
function balanceColumns(ratios: number[], columnCount: number): number[][] {
	const height = (index: number) => ratios[index] + GAP_ALLOWANCE;
	const columns: number[][] = Array.from({ length: columnCount }, () => []);
	const totals = new Array(columnCount).fill(0);

	// Tallest first, each into the currently shortest column
	ratios
		.map((_, index) => index)
		.sort((a, b) => ratios[b] - ratios[a])
		.forEach((index) => {
			const shortest = totals.indexOf(Math.min(...totals));
			columns[shortest].push(index);
			totals[shortest] += height(index);
		});

	// Swap (or move) images between the tallest and shortest columns while it helps
	for (let pass = 0; pass < 50; pass++) {
		const tallest = totals.indexOf(Math.max(...totals));
		const shortest = totals.indexOf(Math.min(...totals));
		const difference = totals[tallest] - totals[shortest];

		let best: { from: number; to: number | null; change: number; remaining: number } | null = null;
		for (const from of columns[tallest]) {
			for (const to of [...columns[shortest], null]) {
				const change = height(from) - (to === null ? 0 : height(to));
				const remaining = Math.abs(difference - 2 * change);
				if (change > 0 && remaining < difference && (!best || remaining < best.remaining)) {
					best = { from, to, change, remaining };
				}
			}
		}
		if (!best) break;

		const { from, to, change } = best;
		columns[tallest] = columns[tallest].filter((index) => index !== from);
		columns[shortest].push(from);
		if (to !== null) {
			columns[shortest] = columns[shortest].filter((index) => index !== to);
			columns[tallest].push(to);
		}
		totals[tallest] -= change;
		totals[shortest] += change;
	}

	// Keep filename order within each column
	return columns.map((column) => column.sort((a, b) => a - b));
}

/**
 * Masonry grid of every case image; clicking one opens it in the lightbox.
 * Images are split into columns of near-equal height so the bottoms line up
 * (CSS columns fill in order, which leaves the bottoms ragged).
 */
export default function CaseGallery() {

	// Index of the image shown in the lightbox, or null when closed
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

	const columnCount = useBreakpointValue({ base: 1, md: 2, lg: 3 }) ?? 1;
	const ratios = useImageRatios(caseImages);

	// Image indexes for each column
	const columns = useMemo(
		() => (ratios ? balanceColumns(ratios, columnCount) : []),
		[ratios, columnCount]
	);

	return (
		<Box
			width={"90%"}
			maxWidth={"1200px"}
			margin={"auto"}
			paddingBottom={{ base: 10, md: 16 }}
			// Hold some space while the image sizes load, so the footer doesn't jump
			minHeight={ratios ? undefined : "60vh"}
		>
			<Flex gap={`${GAP}px`} alignItems={"flex-start"}>
				{columns.map((column, columnIndex) => (
					<Flex
						key={columnIndex}
						flex={1}
						minWidth={0}
						flexDirection={"column"}
						gap={`${GAP}px`}
					>
						{column.map((index) => {
							const src = caseImages[index];

							return (
								// Fade-in wrapper. Hover lives on the inner Box, since framer-motion
								// treats `transition` as its own prop and the CSS transition would be lost.
								<MotionBox
									key={src}
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true, amount: 0.2 }}
									transition={{ duration: 0.5, ease: "easeOut" }}
								>
									<Box
										as={"button"}
										aria-label={`Enlarge past case ${index + 1}`}
										onClick={() => setSelectedIndex(index)}
										display={"block"}
										width={"100%"}
										borderRadius={"md"}
										boxShadow={"lg"}
										overflow={"hidden"}
										transition={"transform 0.2s ease"}
										_hover={{ transform: "scale(1.02)" }}
										_focusVisible={{ boxShadow: "0 0 0 3px #D0A144", outline: "none" }}
									>
										<Image
											src={src}
											alt={`Past case ${index + 1}`}
											width={"100%"}
											height={"auto"}
											display={"block"}
											draggable={false}
										/>
									</Box>
								</MotionBox>
							);
						})}
					</Flex>
				))}
			</Flex>

			<CaseLightbox
				images={caseImages}
				index={selectedIndex}
				onChange={setSelectedIndex}
				onClose={() => setSelectedIndex(null)}
			/>
		</Box>
	);
}
